/**
 * Date-only values (due dates, issue dates, paid dates, expiry dates).
 *
 * A due date is a calendar day, not an instant. The UX-001 bug came from the
 * frontend posting `dueDate + "T00:00:00.000Z"` (UTC midnight): in Central
 * time that instant is the evening of the PREVIOUS day, so the app said
 * Nov 18 while the PDF said Nov 19.
 *
 * Rules (frontend side; the backend has a matching helper):
 *   1. Inputs (`<input type="date">`) hold "YYYY-MM-DD". Post that string as-is
 *      with `dateOnlyForApi()` — never append a time or call toISOString().
 *   2. Read values with `toDateOnly()` — it accepts "YYYY-MM-DD", legacy
 *      UTC-midnight ISO strings, and real instants (converted in the
 *      workspace time zone) and always returns "YYYY-MM-DD".
 *   3. Display with `formatDateOnly()` ("Nov 18, 2026"); compare with
 *      `isPastDateOnly()` / `todayDateOnly()` in the workspace zone.
 */
import { formatInTimeZone } from 'date-fns-tz'
import { getWorkspaceTimezone } from '@/lib/workspace'

export type DateOnly = string // "YYYY-MM-DD"

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/
const UTC_MIDNIGHT_RE = /^(\d{4}-\d{2}-\d{2})T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)$/
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function zone(tz?: string | null): string {
  const candidate = tz || getWorkspaceTimezone() || 'America/Chicago'
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: candidate })
    return candidate
  } catch {
    return 'America/Chicago'
  }
}

export function isDateOnly(value: unknown): boolean {
  if (typeof value !== 'string' || !DATE_ONLY_RE.test(value)) return false
  const [y, m, d] = value.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
}

/**
 * Normalise anything date-ish to "YYYY-MM-DD", or null.
 *  - "2026-11-18"                 → "2026-11-18"
 *  - "2026-11-18T00:00:00.000Z"   → "2026-11-18" (legacy UTC-midnight storage)
 *  - other instants / Date        → that instant's calendar day in the workspace zone
 */
export function toDateOnly(input: string | Date | null | undefined, tz?: string | null): DateOnly | null {
  if (input == null || input === '') return null
  if (typeof input === 'string') {
    if (isDateOnly(input)) return input
    const m = input.match(UTC_MIDNIGHT_RE)
    if (m && isDateOnly(m[1])) return m[1]
  }
  const d = typeof input === 'string' ? new Date(input) : input
  if (Number.isNaN(d.getTime())) return null
  return formatInTimeZone(d, zone(tz), 'yyyy-MM-dd')
}

/** The value to send to the API for a date-only field: "YYYY-MM-DD" or null. */
export function dateOnlyForApi(input: string | Date | null | undefined, tz?: string | null): DateOnly | null {
  return toDateOnly(input, tz)
}

/** Today's calendar day in the workspace zone. */
export function todayDateOnly(tz?: string | null, now: Date = new Date()): DateOnly {
  return formatInTimeZone(now, zone(tz), 'yyyy-MM-dd')
}

export function addDaysDateOnly(value: DateOnly, days: number): DateOnly {
  const [y, m, d] = value.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + days))
  return dt.toISOString().slice(0, 10)
}

/** Negative when a < b. String compare works for ISO dates. */
export function compareDateOnly(a: DateOnly, b: DateOnly): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/** True when the day is strictly before today (workspace zone) — e.g. overdue. */
export function isPastDateOnly(input: string | Date | null | undefined, tz?: string | null, now?: Date): boolean {
  const d = toDateOnly(input, tz)
  return d != null && compareDateOnly(d, todayDateOnly(tz, now)) < 0
}

export type DateOnlyStyle = 'document' | 'short' | 'weekday' | 'long'

/**
 * Display a date-only value. Never shifts the day.
 *  document → "Nov 18, 2026" (house style for documents and lists)
 *  short    → "Nov 18"
 *  weekday  → "Wed, Nov 18"
 *  long     → "November 18, 2026"
 */
export function formatDateOnly(
  input: string | Date | null | undefined,
  style: DateOnlyStyle = 'document',
  tz?: string | null,
): string {
  const d = toDateOnly(input, tz)
  if (!d) return '—'
  const [y, m, day] = d.split('-').map(Number)
  const wd = WEEKDAYS[new Date(Date.UTC(y, m - 1, day)).getUTCDay()]
  switch (style) {
    case 'short': return `${MONTHS[m - 1]} ${day}`
    case 'weekday': return `${wd}, ${MONTHS[m - 1]} ${day}`
    case 'long': return `${MONTHS_LONG[m - 1]} ${day}, ${y}`
    default: return `${MONTHS[m - 1]} ${day}, ${y}`
  }
}
